import type { FormEvent } from 'react';
import { useState } from 'react';

import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/buttons/Button';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { FormInput } from '../../../components/FormInput/FormInput';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';
import { getErrorMessage } from '../../../utils/errors';
import { getEmailErrorMessageMap } from '../SignUpRegister/constants';
import { useOpenAuthWizard } from '../useOpenAuthWizard';

import styles from './ResetPassword.module.scss';

interface ResetPasswordForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    email: HTMLInputElement;
  };
}

export const ResetPassword = observer(({ nextStep }: WizardChildProps) => {
  const { t } = useTranslation();
  const { authStore, portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const isDirectLanding = authStore.isDirectLanding();
  const isSocialLanding = authStore.isSocialLanding();

  const [error, setError] = useState<string>('');
  const isProcessing = computed(() => authStore.pending('sendPasswordResetLink')).get();

  const handleSubmit = async (event: FormEvent<ResetPasswordForm>) => {
    event.preventDefault();
    const { email } = event.currentTarget.elements;

    try {
      void analytics.sendEvent('reset_password', {
        event_action: 'click',
        button_text: 'reset password',
        event_category: 'sign_up',
        event_value: 'enter_email_address',
        event_type: 'reset_password',
        status: 'success',
      });

      await authStore.sendPasswordResetLink(email.value);
      nextStep();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const handleOpenSignUp = () => {
    openAuthWizard('signUpWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: isDirectLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  const handleGoBack = () => {
    if (isSocialLanding) {
      portalStore.closePortal();
      return;
    }

    openAuthWizard('logInWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: isDirectLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  return (
    <>
      <DialogHeader
        closeBtnDataTestId="reset-password-close-btn"
        goBackBtnDataTestId="reset-password-back-btn"
        title={t('auth.resetPassword')}
        onClose={portalStore.closePortal}
        onGoBack={handleGoBack}
      />

      <DialogLoader isProcessing={isProcessing} />

      <DialogContent>
        <form onSubmit={handleSubmit}>
          <p>{t('auth.enterEmailInstructions')}</p>

          <div className={`form-field ${styles.resetPwFormField}`}>
            <FormInput
              required
              data-testid="reset-password-email-input"
              disabled={isProcessing}
              errorMessageMap={getEmailErrorMessageMap(t)}
              id="email"
              name="email"
              placeholder={t('forms.emailAddress')}
              type="email"
            />
          </div>

          <Button
            btnType="main"
            className={styles.resetBtn}
            data-testid="reset-passwod-btn"
            disabled={isProcessing}
            type="submit"
          >
            {t('auth.resetPassword')}
          </Button>

          <ErrorMessage>{error}</ErrorMessage>

          <div className="animation-slide-down form-links">
            <span>{t('auth.dontHaveAccountQuestion')}</span>

            <span className="text-link" onClick={handleOpenSignUp}>
              {t('auth.signUp')}
            </span>
          </div>
        </form>
      </DialogContent>
    </>
  );
});
